/**
 * Dev-only fake data whose single purpose is to make every tag of the app show up
 * at once. A real network almost never carries a cancellation, a deviation, a
 * crowding level and a traffic info on the same screen, so the tag rows can't be
 * reviewed against live data.
 *
 * Enabled with `?mockTags=1` (remembered until `?mockTags=0`). Every call site is
 * written `import.meta.env.DEV && isMockTagsEnabled()` so the whole module is
 * dropped from production bundles.
 */
import {
  LocationType,
  type Calendar,
  type CalendarDate,
  type PickupDropoffType,
  type Stop,
  type Trip,
} from '@/@types/gtfs-ressource';
import { StopType, type DeviationStop, type TripUpdate } from '@/@types/trip-updates';
import type { Alert } from '@/@types/alert';
import type { OnlineDevice } from '@/@types/device';
import type { RailArrival, RailDeparture, RailStationLink } from '@/@types/rail';
import type { Vehicle } from '@/@types/vehicle';

const FLAG_KEY = 'devMockTags';
const MOCK_PREFIX = 'mock-tags';

/**
 * One tag situation per trip. The list is rotated over the trips of the current
 * window, so a stop with a handful of directions shows a different one per row.
 */
const SCENARIOS = [
  'plain',
  'delayed',
  'early',
  'stopDelays',
  'canceled',
  'unserved',
  'displaced',
  'temporary',
  'lowCrowding',
  'moderateCrowding',
  'highCrowding',
  'veryHighCrowding',
  'everything',
] as const;

type Scenario = (typeof SCENARIOS)[number];

const CROWDING_LOAD: { [scenario: string]: number } = {
  lowCrowding: 15,
  moderateCrowding: 45,
  highCrowding: 80,
  veryHighCrowding: 120,
};

/** GTFS `pickup_type` 2: the passenger must call the agency — renders the "À la demande" tag. */
const PICKUP_ON_REQUEST = 2 as PickupDropoffType;

const DELAY_SECONDS = 7 * 60;
const EARLY_SECONDS = -4 * 60;
/** Per-stop delays grow along the trip, one minute per stop, up to this. */
const MAX_STOP_DELAY_SECONDS = 8 * 60;

const UNSERVED_INFORMATION = 'Arrêt non desservi, reportez-vous au suivant.';

const COMMENTS: { [scenario: string]: string } = {
  delayed: 'Trafic dense sur la rocade, retard en cours de résorption.',
  early: 'Véhicule en avance, attente régulée aux arrêts de passage.',
  stopDelays: 'Manifestation en centre-ville, ralentissements progressifs.',
  canceled: 'Course supprimée : véhicule immobilisé.',
  unserved: 'Arrêts non desservis pour cause de travaux.',
  displaced: 'Arrêts reportés de quelques dizaines de mètres.',
  temporary: 'Arrêts provisoires le temps de la déviation.',
  everything: 'Déviation, arrêts non desservis et retard cumulés.',
};

/** A trip is worth mocking while its first departure sits in this window around now. */
const WINDOW_BEFORE_SECONDS = 3600;
const WINDOW_AFTER_SECONDS = 3 * 3600;
/** Keeps the trip-update lookups (a linear search per stop time) and localStorage sane. */
const MAX_MOCKED_TRIPS = 240;
/** Mock devices are drawn on the map as vehicles: only the departures about to leave get one. */
const DEVICE_WINDOW_SECONDS = 30 * 60;
const MAX_MOCKED_DEVICES = 30;

const VEHICLE_ID = `${MOCK_PREFIX}-vehicle`;
const VEHICLE_CAPACITY = 60;

let gtfs: { trips: { [tripId: string]: Trip }; stops: { [stopId: string]: Stop } } | null = null;
let scenarios: { computedAt: number; byTripId: Map<string, Scenario> } | null = null;
const readyCallbacks = new Set<() => void>();

let enabled: boolean | null = null;

/** Dev builds only, on `?mockTags=1` in the URL (plain or hash query) or a previous one. */
export function isMockTagsEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  if (enabled !== null) return enabled;

  const hashQuery = window.location.hash.split('?')[1] ?? '';
  const asked =
    new URLSearchParams(window.location.search).get('mockTags') ??
    new URLSearchParams(hashQuery).get('mockTags');

  if (asked !== null) {
    enabled = asked !== '0' && asked !== 'false';
    localStorage.setItem(FLAG_KEY, enabled ? '1' : '0');
  } else {
    enabled = localStorage.getItem(FLAG_KEY) === '1';
  }
  if (enabled) console.info('[mock-tags] enabled — add ?mockTags=0 to the URL to turn it off');
  return enabled;
}

/**
 * Registers a callback fired once the GTFS is in: the alerts, the station links and
 * the first trip-update poll all run before it, and their mocks are built from it.
 */
export function onMockTagsReady(callback: () => void) {
  readyCallbacks.add(callback);
}

type MockableGtfs = {
  trips?: { [tripId: string]: Trip };
  stops?: { [stopId: string]: Stop };
  calendars?: { [serviceId: string]: Calendar };
  calendarDates?: Array<CalendarDate>;
};

/**
 * Rewrites the accessibility, bike, platform and on-request attributes of the whole
 * network, so the tags they produce are on every stop sheet and every trip, and
 * makes sure a service runs today, or no sheet would have anything to tag.
 * Mutates the GTFS in place, before the store freezes it — the IndexedDB copy is
 * already written at that point and stays untouched.
 */
export function decorateGtfsForTags(gtfsToDecorate: MockableGtfs) {
  const trips = gtfsToDecorate.trips ?? {};
  const stops = gtfsToDecorate.stops ?? {};
  if (Object.keys(trips).length === 0) return;

  runOneServiceToday(gtfsToDecorate, trips);

  Object.values(trips).forEach((trip, index) => {
    trip.wheelchair_accessible = index % 2 === 0 ? 1 : 0;
    trip.bikes_allowed = index % 3 === 0 ? 1 : 0;
    // Never the terminuses: an on-request first or last stop reads as a data error.
    trip.stop_times?.forEach((stopTime, stopIndex) => {
      if (stopIndex > 0 && stopIndex < trip.stop_times.length - 1 && stopIndex % 4 === 1) {
        stopTime.pickup_type = PICKUP_ON_REQUEST;
      }
    });
  });

  Object.values(stops).forEach((stop, index) => {
    stop.wheelchair_boarding = index % 2 === 0 ? 1 : 0;
    if (index % 3 === 0) stop.platform_code = String((index % 12) + 1);
  });

  gtfs = { trips, stops };
  scenarios = null;
  readyCallbacks.forEach(callback => callback());
  logScenarios();
}

/**
 * A stale dev GTFS often has no service left for today, and every sheet reads from
 * the trips of the day: the mocked updates would then target trips nobody can open.
 * Runs the service with the most trips in the mock window — a single one, since
 * several service families at once duplicate every departure.
 */
function runOneServiceToday(gtfsToDecorate: MockableGtfs, trips: { [tripId: string]: Trip }) {
  const today = gtfsToday();
  if (hasServiceOn(today, gtfsToDecorate)) return;

  const nowSeconds = secondsSinceMidnight();
  const tripsByService = new Map<string, number>();
  Object.values(trips).forEach(trip => {
    const departure = trip.stop_times?.[0]?.departure_time;
    if (departure === undefined) return;
    if (departure < nowSeconds - WINDOW_BEFORE_SECONDS || departure > nowSeconds + WINDOW_AFTER_SECONDS)
      return;
    tripsByService.set(trip.service_id, (tripsByService.get(trip.service_id) ?? 0) + 1);
  });
  const busiest = [...tripsByService.entries()].sort((a, b) => b[1] - a[1])[0];
  if (!busiest) return;

  const [serviceId] = busiest;
  gtfsToDecorate.calendarDates = [
    ...(gtfsToDecorate.calendarDates ?? []),
    { service_id: serviceId, date: today, exception_type: 1 },
  ];
  console.info(
    `[mock-tags] no service today in this GTFS: running "${serviceId}" so the sheets have trips to tag`,
  );
}

/** Same resolution as the GTFS store's trips of the day: weekly calendars, then their exceptions. */
function hasServiceOn(dateGtfs: string, gtfsToDecorate: MockableGtfs): boolean {
  const weekday = (['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const)[
    new Date().getDay()
  ];
  const services = new Set(
    Object.values(gtfsToDecorate.calendars ?? {})
      .filter(
        calendar =>
          calendar.start_date <= dateGtfs && calendar.end_date >= dateGtfs && calendar[weekday] === 1,
      )
      .map(calendar => calendar.service_id),
  );
  (gtfsToDecorate.calendarDates ?? [])
    .filter(exception => exception.date === dateGtfs)
    .forEach(exception => {
      if (exception.exception_type === 1) services.add(exception.service_id);
      else if (exception.exception_type === 2) services.delete(exception.service_id);
    });
  return services.size > 0;
}

export function mockTripUpdates(date: string, groupId: string, gtfsId: string): Array<TripUpdate> {
  if (!gtfs) return [];
  const updates: Array<TripUpdate> = [];
  activeScenarios().forEach((scenario, tripId) => {
    const update = buildTripUpdate(gtfs!.trips[tripId], scenario, date, groupId, gtfsId);
    if (update) updates.push(update);
  });
  return updates;
}

export function mockOnlineDevices(filters: {
  routeIds?: Array<string>;
  deviceId?: string;
  tripId?: string;
}): Array<OnlineDevice> {
  if (!gtfs) return [];
  const nowSeconds = secondsSinceMidnight();
  const devices: Array<OnlineDevice> = [];

  activeScenarios().forEach((scenario, tripId) => {
    if (devices.length >= MAX_MOCKED_DEVICES) return;
    const load = CROWDING_LOAD[scenario];
    if (load === undefined) return;
    const trip = gtfs!.trips[tripId];
    const firstStopTime = trip?.stop_times?.[0];
    if (!firstStopTime) return;
    if (Math.abs(firstStopTime.departure_time - nowSeconds) > DEVICE_WINDOW_SECONDS) return;

    const stop = gtfs!.stops[firstStopTime.stop_id];
    devices.push({
      device_id: `${MOCK_PREFIX}-${tripId}`,
      trip_id: tripId,
      route_id: trip.route_id,
      latlng: [stop?.stop_lat ?? 0, stop?.stop_lon ?? 0],
      bearing: 45,
      current_stop_sequence: firstStopTime.stop_sequence,
      delay: 0,
      load_percentage: load,
      last_vehicle_associated: VEHICLE_ID,
    });
  });

  return devices.filter(device => {
    if (filters.deviceId && device.device_id !== filters.deviceId) return false;
    if (filters.tripId && device.trip_id !== filters.tripId) return false;
    if (filters.routeIds?.length && !filters.routeIds.includes(device.route_id ?? '')) return false;
    return true;
  });
}

/** The crowding tags need a capacity to divide by, whatever the real fleet holds. */
export function mockVehicles(): Array<Vehicle> {
  return [{ _id: VEHICLE_ID, id: VEHICLE_ID, capacity: VEHICLE_CAPACITY }];
}

/**
 * Two alerts on the first route and two on one of its stops: the departure cards
 * read "2 infos trafic", and the stop sheet its own counter (it shows all but the
 * one it expands in place).
 */
export function mockAlerts(): Array<Alert> {
  if (!gtfs) return [];
  const trip = Object.values(gtfs.trips).find(t => t.stop_times?.length > 2);
  if (!trip) return [];
  const stopId = trip.stop_times[1].stop_id;
  const today = gtfsToday();
  const window = {
    start_date: today,
    start_time: '00:00:00',
    end_date: today,
    end_time: '23:59:59',
  };

  return [
    {
      _id: `${MOCK_PREFIX}-alert-1`,
      id: `${MOCK_PREFIX}-alert-1`,
      active: true,
      effect: 4,
      header_text: 'Déviation pour travaux avenue de la Gare',
      description_text: 'Les véhicules sont déviés par le boulevard jusqu’à la fin des travaux.',
      url: 'https://www.pysae.com',
      active_date_times: [window],
      informed_entity: [{ route_id: trip.route_id }, { stop_id: stopId }],
    },
    {
      _id: `${MOCK_PREFIX}-alert-2`,
      id: `${MOCK_PREFIX}-alert-2`,
      active: true,
      effect: 3,
      header_text: 'Retards importants en heure de pointe',
      description_text: 'Un incident de circulation ralentit la ligne depuis ce matin.',
      active_date_times: [window],
      informed_entity: [{ route_id: trip.route_id }],
    },
    {
      _id: `${MOCK_PREFIX}-alert-3`,
      id: `${MOCK_PREFIX}-alert-3`,
      active: true,
      effect: 9,
      header_text: 'Arrêt déplacé de 100 mètres',
      active_date_times: [window],
      informed_entity: [{ stop_id: stopId }],
    },
  ];
}

/** Attaches the first stops to a station, so their sheet opens the train board. */
export function mockRailStationLinks(): Array<RailStationLink> {
  if (!gtfs) return [];
  return Object.values(gtfs.stops)
    .filter(stop => stop.location_type !== LocationType.STATION)
    .slice(0, 5)
    .map(stop => ({
      stop_id: stop.stop_id,
      uic: `87${stop.stop_id}`.slice(0, 8),
      stop_name: stop.stop_name,
      created_at: new Date().toISOString(),
      is_orphan: false,
      ingestion_status: 'ready' as const,
    }));
}

/** One train per status, so the board shows the cancelled tag next to its siblings. */
export function mockRailDepartures(stopId: string): Array<RailDeparture> {
  const statuses: Array<{ status: RailDeparture['status']; delaySeconds?: number }> = [
    { status: 'on_time' },
    { status: 'delayed', delaySeconds: 11 * 60 },
    { status: 'canceled' },
    { status: 'early', delaySeconds: -2 * 60 },
    { status: 'scheduled' },
    { status: 'added' },
    { status: 'skipped' },
  ];
  const destinations = ['Paris Montparnasse', 'Quimper', 'Nantes', 'Rennes', 'Brest', 'Lorient', 'Vannes'];

  return statuses.map(({ status, delaySeconds }, index) => {
    const scheduled = new Date(Date.now() + (index * 9 + 4) * 60000);
    const estimated = new Date(scheduled.getTime() + (delaySeconds ?? 0) * 1000);
    return {
      trip_id: `${MOCK_PREFIX}-train-${index}`,
      service_date: gtfsToday(),
      uic: `87${stopId}`.slice(0, 8),
      station_name: 'Gare de démonstration',
      stop_id: `${MOCK_PREFIX}-rail-${stopId}`,
      platform: String((index % 4) + 1),
      scheduled_departure: scheduled.toISOString(),
      estimated_departure: delaySeconds === undefined ? null : estimated.toISOString(),
      delay_seconds: delaySeconds ?? null,
      status,
      route_id: `${MOCK_PREFIX}-ter`,
      // A replacement coach every third train, for the bus icon of the card.
      route_type: index % 3 === 2 ? 3 : 2,
      route_short_name: index % 3 === 2 ? 'Car TER' : 'TER',
      route_long_name: `${index % 2 ? 'Nantes' : 'Rennes'} - ${destinations[index]}`,
      trip_headsign: destinations[index],
      trip_short_name: String(855100 + index),
      destination: destinations[index],
      source_id: `${MOCK_PREFIX}-source`,
    };
  });
}

/** The mirror of the departures, with a train ending its trip here (no departure time). */
export function mockRailArrivals(stopId: string): Array<RailArrival> {
  const statuses: Array<{ status: RailArrival['status']; delaySeconds?: number }> = [
    { status: 'delayed', delaySeconds: 6 * 60 },
    { status: 'on_time' },
    { status: 'scheduled' },
    { status: 'canceled' },
  ];
  const origins = ['Rennes', 'Brest', 'Nantes', 'Quimper'];

  return statuses.map(({ status, delaySeconds }, index) => {
    const scheduled = new Date(Date.now() + (index * 12 + 2) * 60000);
    const estimated = new Date(scheduled.getTime() + (delaySeconds ?? 0) * 1000);
    const isTerminus = index === 2;
    return {
      trip_id: `${MOCK_PREFIX}-arrival-${index}`,
      service_date: gtfsToday(),
      uic: `87${stopId}`.slice(0, 8),
      station_name: 'Gare de démonstration',
      stop_id: `${MOCK_PREFIX}-rail-${stopId}`,
      scheduled_arrival: scheduled.toISOString(),
      scheduled_departure: isTerminus ? null : new Date(scheduled.getTime() + 2 * 60000).toISOString(),
      estimated_arrival: delaySeconds === undefined ? null : estimated.toISOString(),
      delay_seconds: delaySeconds ?? null,
      status,
      route_id: `${MOCK_PREFIX}-ter`,
      route_type: index === 3 ? 3 : 2,
      route_short_name: index === 3 ? 'Car TER' : 'TER',
      route_long_name: `${origins[index]} - ${isTerminus ? 'Gare de démonstration' : 'Lorient'}`,
      trip_headsign: isTerminus ? 'Gare de démonstration' : 'Lorient',
      trip_short_name: String(855200 + index),
      origin: origins[index],
      source_id: `${MOCK_PREFIX}-source`,
    };
  });
}

/**
 * The trips worth mocking right now, each with its scenario. Recomputed with the
 * trip-update poll so the tags follow the departures the passenger is looking at,
 * and shared with the devices so both feeds agree on what a trip is doing.
 */
function activeScenarios(): Map<string, Scenario> {
  if (scenarios && Date.now() - scenarios.computedAt < 60000) return scenarios.byTripId;

  const byTripId = new Map<string, Scenario>();
  if (!gtfs) return byTripId;

  const nowSeconds = secondsSinceMidnight();
  const candidates = Object.values(gtfs.trips)
    .filter(trip => {
      const departure = trip.stop_times?.[0]?.departure_time;
      if (departure === undefined) return false;
      return departure > nowSeconds - WINDOW_BEFORE_SECONDS && departure < nowSeconds + WINDOW_AFTER_SECONDS;
    })
    .sort((a, b) => a.trip_id.localeCompare(b.trip_id));

  // Sampled rather than truncated: a big network would otherwise mock a single
  // route's morning and leave every other sheet bare.
  const stride = Math.max(1, Math.ceil(candidates.length / MAX_MOCKED_TRIPS));
  candidates
    .filter((_, index) => index % stride === 0)
    .forEach((trip, index) => byTripId.set(trip.trip_id, SCENARIOS[index % SCENARIOS.length]));

  scenarios = { computedAt: Date.now(), byTripId };
  return byTripId;
}

function buildTripUpdate(
  trip: Trip | undefined,
  scenario: Scenario,
  date: string,
  groupId: string,
  gtfsId: string,
): TripUpdate | null {
  if (!trip?.stop_times?.length) return null;

  const update = {
    date,
    group_id: groupId,
    gtfs_id: gtfsId,
    trip_id: trip.trip_id,
    comment: null,
    delay: 0,
    is_canceled: false,
    skipped_stop_time_seqs: [],
    stop_infos: null,
    delays: [],
    temporary_stops: [],
    // `shape` only set on the deviations below: an empty one would replace the
    // trip's real shape on the map with nothing (see `getUpdatedShape`).
  } as unknown as TripUpdate;

  switch (scenario) {
    case 'delayed':
      update.delay = DELAY_SECONDS;
      break;
    case 'early':
      update.delay = EARLY_SECONDS;
      break;
    case 'stopDelays':
      update.delays = stopDelays(trip);
      break;
    case 'canceled':
      update.is_canceled = true;
      break;
    case 'unserved':
      update.skipped_stop_time_seqs = skippedSequences(trip);
      update.stop_infos = stopInfos(update.skipped_stop_time_seqs, UNSERVED_INFORMATION);
      break;
    case 'displaced':
      update.temporary_stops = displacedStops(trip);
      update.stop_infos = stopInfos(
        update.temporary_stops.map(stop => stop.stop_sequence),
        'Arrêt reporté au coin de la rue.',
      );
      update.shape = deviatedShape(trip, update.temporary_stops);
      break;
    case 'temporary':
      update.temporary_stops = temporaryStops(trip);
      update.shape = deviatedShape(trip, update.temporary_stops);
      break;
    case 'everything':
      update.delay = DELAY_SECONDS;
      update.delays = stopDelays(trip);
      update.skipped_stop_time_seqs = skippedSequences(trip);
      update.stop_infos = stopInfos(update.skipped_stop_time_seqs, UNSERVED_INFORMATION);
      update.temporary_stops = [...displacedStops(trip), ...temporaryStops(trip)];
      update.shape = deviatedShape(trip, update.temporary_stops);
      break;
    default:
      return null;
  }
  update.comment = COMMENTS[scenario] ?? null;
  return update;
}

/** No delay at the origin: a trip can't leave late from a stop it hasn't reached yet. */
function stopDelays(trip: Trip): TripUpdate['delays'] {
  return trip.stop_times.slice(1).map((stopTime, index) => ({
    stop_sequence: stopTime.stop_sequence,
    delay: Math.min((index + 1) * 60, MAX_STOP_DELAY_SECONDS),
  }));
}

function stopInfos(sequences: Array<number>, information: string): TripUpdate['stop_infos'] {
  return sequences.map(sequence => ({ stop_sequence: sequence, information }));
}

/**
 * The trip's polyline redrawn through its deviated stops, in GeoJSON order
 * (`[lon, lat]`) like the GTFS shape it replaces on the map.
 */
function deviatedShape(trip: Trip, temporaryStops: Array<DeviationStop>): Array<[number, number]> {
  const shifted = new Map(
    temporaryStops.filter(stop => stop.type === StopType.SHIFT).map(stop => [stop.stop_sequence, stop]),
  );
  const served = trip.stop_times.flatMap(stopTime => {
    const stop = shifted.get(stopTime.stop_sequence) ?? gtfs?.stops[stopTime.stop_id];
    if (!stop) return [];
    return [{ stop_sequence: stopTime.stop_sequence, stop_lat: stop.stop_lat, stop_lon: stop.stop_lon }];
  });
  const inserted = temporaryStops.filter(stop => stop.type !== StopType.SHIFT);
  return [...served, ...inserted]
    .sort((a, b) => a.stop_sequence - b.stop_sequence)
    .map(point => [point.stop_lon, point.stop_lat]);
}

/** Never the terminuses: a trip skipping its own ends is a shortened trip, not a deviation. */
function skippedSequences(trip: Trip): Array<number> {
  return trip.stop_times
    .slice(1, -1)
    .slice(0, 2)
    .map(stopTime => stopTime.stop_sequence);
}

function displacedStops(trip: Trip): Array<DeviationStop> {
  return trip.stop_times
    .slice(1, -1)
    .slice(0, 2)
    .flatMap(stopTime => {
      const stop = gtfs?.stops[stopTime.stop_id];
      if (!stop) return [];
      return [
        {
          arrival_time: stopTime.arrival_time,
          departure_time: stopTime.departure_time,
          stop_sequence: stopTime.stop_sequence,
          // Enough to read as another corner of the street at the map's zoom.
          stop_lat: stop.stop_lat + 0.0012,
          stop_lon: stop.stop_lon + 0.0012,
          stop_name: stop.stop_name,
          stop_id: stop.stop_id,
          type: StopType.SHIFT,
        },
      ];
    });
}

/** One stop invented for the trip, one borrowed from elsewhere in the network. */
function temporaryStops(trip: Trip): Array<DeviationStop> {
  const anchor = trip.stop_times[Math.min(2, trip.stop_times.length - 2)];
  const next = trip.stop_times[trip.stop_times.indexOf(anchor) + 1] ?? anchor;
  const anchorStop = gtfs?.stops[anchor.stop_id];
  if (!anchorStop) return [];

  const served = new Set(trip.stop_times.map(stopTime => stopTime.stop_id));
  const reused = Object.values(gtfs?.stops ?? {}).find(
    stop => stop.location_type !== LocationType.STATION && !served.has(stop.stop_id),
  );

  const middle = Math.round((anchor.departure_time + next.departure_time) / 2);
  const stops: Array<DeviationStop> = [
    {
      arrival_time: middle,
      departure_time: middle,
      // Half sequences: the trip page sorts its stops on this, and the inserted
      // ones must land between their neighbours without renumbering the trip.
      stop_sequence: anchor.stop_sequence + 0.5,
      stop_lat: anchorStop.stop_lat + 0.0018,
      stop_lon: anchorStop.stop_lon - 0.0018,
      stop_name: 'Arrêt provisoire Mairie',
      stop_id: `${MOCK_PREFIX}-adhoc-${trip.trip_id}`,
      type: StopType.AD_HOC,
    },
  ];

  if (reused) {
    stops.push({
      arrival_time: middle + 60,
      departure_time: middle + 60,
      stop_sequence: anchor.stop_sequence + 0.6,
      stop_lat: reused.stop_lat,
      stop_lon: reused.stop_lon,
      stop_name: reused.stop_name,
      stop_id: reused.stop_id,
      type: StopType.REUSE,
    });
  }
  return stops;
}

function secondsSinceMidnight(): number {
  const now = new Date();
  return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
}

function gtfsToday(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}${month}${day}`;
}

/** One trip id per scenario, so a tag can be opened straight from the console. */
function logScenarios() {
  const examples: { [scenario: string]: string } = {};
  activeScenarios().forEach((scenario, tripId) => {
    if (!examples[scenario]) examples[scenario] = tripId;
  });
  console.info('[mock-tags] one trip per tag situation (#/<groupId>/map/trip-detailed/<tripId>)', examples);
}
